import React from 'react';

/**
 * useInfiniteScroll
 * Reusable hook that triggers onLoadMore when the sentinel enters viewport.
 * Handles de-duping while loading and resets when user scrolls up.
 *
 * @param {Object} params
 * @param {boolean} params.isLoading - current loading state
 * @param {boolean} params.hasMore - whether more data is available
 * @param {Function} params.onLoadMore - callback to load next page
 * @param {string} [params.rootMargin='200px 0px'] - observer rootMargin
 * @param {number} [params.threshold=0] - observer threshold
 */
export default function useInfiniteScroll({
  isLoading,
  hasMore,
  onLoadMore,
  rootMargin = '200px 0px',
  threshold = 0
}) {
  const sentinelRef = React.useRef(null);
  const loadingRef = React.useRef(false);
  const pendingRef = React.useRef(false);
  const seenRef = React.useRef(false);
  const lastScrollYRef = React.useRef(0);

  React.useEffect(() => {
    loadingRef.current = isLoading;
    if (!isLoading) {
      pendingRef.current = false;
      // Reset seenRef when loading completes so we can trigger again if sentinel is still visible
      seenRef.current = false;
    }
  }, [isLoading]);

  React.useEffect(() => {
    if (!hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (!loadingRef.current && !pendingRef.current && !seenRef.current) {
              seenRef.current = true;
              pendingRef.current = true;
              onLoadMore?.();
            }
          } else {
            seenRef.current = false;
          }
        }
      },
      { root: null, rootMargin, threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, rootMargin, threshold]);

  React.useEffect(() => {
    let rafId = 0;
    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const current = window.pageYOffset || window.scrollY || 0;
        if (current < lastScrollYRef.current) {
          seenRef.current = false;
        }
        lastScrollYRef.current = current;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  React.useEffect(() => {
    if (isLoading || !hasMore) return;
    const node = sentinelRef.current;
    if (!node) return;

    // Small delay to ensure DOM has updated after data load
    const timeoutId = setTimeout(() => {
      const topMarginToken = String(rootMargin).trim().split(/\s+/)[0] || '0px';
      const marginTop = parseInt(topMarginToken, 10) || 0;

      const rect = node.getBoundingClientRect();
      const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
      const isVisible = rect.top <= viewportH + marginTop && rect.bottom >= -marginTop;

      if (isVisible && !pendingRef.current && !loadingRef.current) {
        seenRef.current = true;
        pendingRef.current = true;
        onLoadMore?.();
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [isLoading, hasMore, onLoadMore, rootMargin]);

  return { sentinelRef };
}
